import type { SupplierLookup } from '../types/supplier-lookup.type';
import type { SustainabilitySummary } from '../types/supplier/sustainability-summary.type';
import { BaseModel } from './base.model';
import { EntityStatusModel } from './entity-status.model';
import { GuidNameCodeModel } from './guid-name-code.model';
import { IdNameCodeModel } from './id-name-code.model';
import { IdNameModel } from './id-name.model';
import { SupplierClassificationStatusModel } from './supplier/supplier-classification-status.model';
import { SupplierMdfQualificationStatusModel } from './supplier/supplier-mdf-qualification-status.model';
import { TermsAndConditionsModel } from './terms-and-conditions.model';
import { UserModel } from './user.model';

export class SupplierLookupModel extends BaseModel implements SupplierLookup {
  readonly id: string;
  readonly name: string | null;
  readonly guid: string | null;
  readonly smVendorId: string | null;
  readonly isActive: boolean | null;
  readonly country: IdNameCodeModel | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly zipCode: string | null;
  readonly address: string | null;
  readonly website: string | null;
  readonly qualificationStatus: EntityStatusModel | null;
  readonly mdfQualificationStatus: SupplierMdfQualificationStatusModel | null;
  readonly registrationStatus: EntityStatusModel | null;
  readonly classificationStatus: IdNameModel | null;
  readonly classificationStatuses: SupplierClassificationStatusModel[];
  readonly kpiRiskValue: number | null;
  readonly kpiContractsCount: number | null;
  readonly leadBuyers: UserModel[];
  readonly defaultTermsAndConditions: TermsAndConditionsModel | null;
  readonly latitude: number | null;
  readonly longitude: number | null;
  readonly hasFrameAgreement: boolean | null;
  readonly type: EntityStatusModel | null;
  readonly averageEvaluationScore: number | null;
  readonly existsInSap: boolean | null;
  readonly parentSupplier: GuidNameCodeModel | null;
  readonly isTcd: boolean;
  readonly isEtcd: boolean;
  readonly isDuplicate: boolean;
  readonly levels: string[];
  readonly sustainability: SustainabilitySummary | null;

  constructor(data: SupplierLookup) {
    super();

    this.id = data.id;
    this.name = data.name;
    this.guid = data.guid ?? null;
    this.smVendorId = data.smVendorId ?? null;
    this.isActive = data.isActive ?? null;

    this.country = data.country ? new IdNameCodeModel(data.country) : null;

    this.state = data.state ?? null;
    this.city = data.city ?? null;
    this.zipCode = data.zipCode ?? null;
    this.address = data.address ?? null;
    this.website = data.website ?? null;
    this.hasFrameAgreement = data.hasFrameAgreement ?? null;
    this.averageEvaluationScore = data.averageEvaluationScore ?? null;

    this.qualificationStatus = data.qualificationStatus
      ? new EntityStatusModel(data.qualificationStatus)
      : null;

    this.mdfQualificationStatus = data.mdfQualificationStatus
      ? new SupplierMdfQualificationStatusModel(data.mdfQualificationStatus)
      : null;

    this.registrationStatus = data.registrationStatus
      ? new EntityStatusModel(data.registrationStatus)
      : null;

    this.classificationStatus = data.classificationStatus
      ? new IdNameModel(data.classificationStatus)
      : null;

    this.classificationStatuses =
      data.classificationStatuses?.map(
        (item) => new SupplierClassificationStatusModel(item),
      ) ?? [];

    this.kpiRiskValue = data.kpiRiskValue ?? null;
    this.kpiContractsCount = data.kpiContractsCount ?? null;

    this.leadBuyers = data.leadBuyers?.map((item) => new UserModel(item)) ?? [];

    this.defaultTermsAndConditions = data.defaultTermsAndConditions
      ? new TermsAndConditionsModel(data.defaultTermsAndConditions)
      : null;

    this.latitude = data.latitude ?? null;
    this.longitude = data.longitude ?? null;

    this.type = data.type ? new EntityStatusModel(data.type) : null;

    this.existsInSap = data.existsInSap ?? null;

    this.parentSupplier = data.parentSupplier
      ? new GuidNameCodeModel(data.parentSupplier)
      : null;

    this.isTcd = data.isTcd ?? false;
    this.isEtcd = data.isEtcd ?? false;
    this.isDuplicate = data.isDuplicate ?? false;
    this.levels = data.levels?.map((level) => level.toUpperCase()) ?? [];
    this.sustainability = data.sustainability ?? null;
  }

  getDisplay(): string {
    return [this.name, this.guid, this.city].filter(Boolean).join(' - ');
  }
}
